/**
 * Success message generator
 * @param {Function} translator - i18n translation function
 * @param {string} operation - message key suffix
 * @param {string|null} parameter - optional item to interpolate
 * @returns {string}
 */
export function getSuccessMessages(translator, operation, parameter) {
    return parameter != null
        ? translator(`messages.success:${operation}`, { item: parameter })
        : translator(`messages.success:${operation}`);
}

/**
 * Warning message generator
 */
export function getWarningMessages(translator, operation, parameter) {
    return parameter != null
        ? translator(`messages.warning:${operation}`, { item: parameter })
        : translator(`messages.warning:${operation}`);
}

/**
 * Info message generator
 */
export function getInfoMessages(translator, operation, parameterName, parameterValue) {
    if (parameterName != null) {
        const tempObj = { [parameterName]: parameterValue };
        return translator(`messages.info:${operation}`, tempObj);
    }
    return translator(`messages.info:${operation}`);
}

/**
 * Error message generator from result code
 */
export function getErrorMessagesFromCode(translator, resultCode, isNormal) {
    const generic = translator('messages.error:generic', { item: 'Please contact your administrator.' });
    const codeSuffix = ` Error code: ${resultCode}`;

    const errorMap = {
        1005: translator('messages.error:generic', { item: 'proxyUserNoPermission' }),
        2004: translator('messages.error:generic', { item: 'There are not enough licenses for this role.' }),
        2019: translator('messages.error:generic', { item: 'InvalidMinimumPasswordLength' }),
        3001: translator('messages.error:genericPleaseTryAgain', { item: 'The email address already exists!' }),
        3010: translator('messages.error:codeRequestFailed'),
        3013: isNormal
            ? translator('messages.error:generic', { item: 'wrongPassword' }) + ' ' + translator('messages.common:error')
            : translator('messages.error:incorrectPassword') + ' ' + translator('messages.common:error'),
        3017: isNormal
            ? translator('messages.error:generic', { item: 'passwordHistory' })
            : translator('messages.error:passwordHistoryError') + ' ' + translator('messages.common:error'),
        5001: translator('messages.error:projectNameAlreadyExists') + ' ' + translator('messages.common:error'),
        7006: translator('messages.error:genericPleaseTryAgain', {
            item: 'Selection could not be saved because a parameter was not set for a selected KPI Card.'
        }),
        8002: translator('messages.error:general', { item: resultCode })
    };

    return errorMap[resultCode] ? errorMap[resultCode] + codeSuffix : generic + codeSuffix;
}

/**
 * Error message from HTTP status or exception
 */
export function getErrorMsgFromStatus(translator, e, isNormal) {
    if (isNormal) {
        if (e?.toString().includes('Request failed with status code 500')) {
            return translator('messages.error:general', { item: '500' });
        } else if (e?.status) {
            return translator('messages.error:general', { item: e.status });
        } else {
            return translator('messages.error:generic', { item: 'Please contact your administrator.' });
        }
    } else {
        return translator('messages.error:requestCouldProcess');
    }
}

/**
 * Generic fallback error
 */
export function getGenericServerErrorMsg(translator) {
    return translator('messages.error:generic', { item: 'Please contact your administrator.' });
}

/**
 * Specific operation error message
 */
export function getSpecificOPErrorMessages(translator, operation, parameter, resultCode) {
    const opMap = {
        FAILED_TFA_INVALID_ACCESS_TOKEN: 'messages.error:failedTfaInvalidAccessToken',
        FAILED_TFA_INVALID_TOKEN: 'messages.error:failedTfaInvalidToken',
        FAILED_TFA_TOKEN_EXPIRED: 'messages.error:failedTfaTokenExpired',
        INVALID_PARAMETER: 'messages.error:invalidParameter',
        FAILED_EXPORT_ENCODING: 'messages.error:invalidEncoding'
    };

    if (opMap[operation]) {
        return translator(opMap[operation]);
    }

    const temp = parameter != null
        ? translator(`messages.error:${operation}`, { item: parameter })
        : translator(`messages.error:${operation}`);

    return resultCode != null ? `${temp} Error code: ${resultCode}` : temp;
}